import {
  resolveAffectedScheduleBlocks,
  resolveScheduleColours,
  trackOf,
} from '@weareopenr/common/build/src/validationHelper/timetable-schedule';
import { ITimetable } from 'src/api/getTimetables';
import { ITimetableSchedule } from 'src/api/getTimetable';
import { IMenu } from 'src/types/menu.types';
import { IMenuSet } from 'src/api/getMenuSets';
import { IPriceScheme } from 'src/types/price.types';
import { filterMenusByRanges, filterMenuSetsByRanges, isMenuTrackSchedule } from './helpers';

export interface TimetableDetailsPreviewContext {
  // A menu's or a menu set's id - the menu track's default can be either.
  defaultMenuId: string;
  defaultPriceSchemeId: string;
  ranges: ITimetable['ranges'];
  menus: IMenu[];
  menuSets?: IMenuSet[];
  priceSchemes: IPriceScheme[];
}

const trackOfSchedule = (schedule: ITimetableSchedule): 'menu' | 'price_scheme' =>
  isMenuTrackSchedule(schedule) ? 'menu' : 'price_scheme';

// resolveAffectedScheduleBlocks only reassigns rows that currently stand in for the OLD default
// (colour_order === null) or fall outside newly submitted ranges - it never looks at rows that are
// already explicitly scheduled to whatever the NEW default turns out to be. So a day/track that's
// already fully covered by explicit (non-default) entities never shows up as "affected" even when
// one of those explicit rows' entity is the one just picked as default, and that row is left with
// its old, non-null colour_order - i.e. never promoted to "Default" or highlighted. This pass
// catches that case by promoting any schedule whose entity now matches the new default, regardless
// of whether resolveAffectedScheduleBlocks touched its group.
const promoteExplicitDefaultMatch = (
  schedule: ITimetableSchedule,
  defaultMenuId: string,
  defaultPriceSchemeId: string,
): ITimetableSchedule => {
  const isNewDefaultEntity =
    (isMenuTrackSchedule(schedule) &&
      defaultMenuId !== '' &&
      (schedule.menu_id ?? schedule.menu_set_id) === defaultMenuId) ||
    (schedule.price_scheme_id !== null &&
      defaultPriceSchemeId !== '' &&
      schedule.price_scheme_id === defaultPriceSchemeId);

  return isNewDefaultEntity && schedule.colour_order !== null ? { ...schedule, colour_order: null } : schedule;
};

/**
 * Builds the schedules a timetable would actually have if the given defaults/ranges were saved,
 * using the shared `resolveAffectedScheduleBlocks`/`resolveScheduleColours` - the same logic the
 * API applies - rather than a hand-rolled FE approximation. A previous version of this preview
 * only relabelled which existing row counted as "Default" without touching its entity, so a
 * schedule reassigned to a brand new default (e.g. a price scheme with no schedule of its own
 * yet) kept showing its old name - this rebuilds the affected rows so the preview actually shows
 * what's changing, not just that something did.
 */
export const buildTimetableDetailsPreviewSchedules = (
  schedules: ITimetableSchedule[],
  { defaultMenuId, defaultPriceSchemeId, ranges, menus, menuSets = [], priceSchemes }: TimetableDetailsPreviewContext,
): ITimetableSchedule[] => {
  const isDefaultMenuSet = menuSets.some((menuSet) => menuSet.menu_set_id === defaultMenuId);
  const defaultMenuFields = {
    menuId: !isDefaultMenuSet && defaultMenuId ? defaultMenuId : null,
    menuSetId: isDefaultMenuSet ? defaultMenuId : null,
  };
  const affectedBlocks = resolveAffectedScheduleBlocks(schedules, {
    newDefaultMenuId: defaultMenuFields.menuId,
    newDefaultMenuSetId: defaultMenuFields.menuSetId,
    newDefaultPriceSchemeId: defaultPriceSchemeId || null,
    rangeIdsSubmitted: ranges.length > 0,
    effectiveRangeIds: ranges.map((range) => range.range_id),
    menusValidForRanges: new Set(filterMenusByRanges(menus, ranges).map((menu) => menu.menuId)),
    menuSetsValidForRanges: new Set(filterMenuSetsByRanges(menuSets, ranges).map((menuSet) => menuSet.menu_set_id)),
  });

  const baseSchedules = ((): ITimetableSchedule[] => {
    if (affectedBlocks.length === 0) return schedules;

    const affectedGroups = new Set(affectedBlocks.map((block) => `${block.day_of_week}:${trackOf(block)}`));
    const untouchedSchedules = schedules.filter(
      (schedule) => !affectedGroups.has(`${schedule.day_of_week}:${trackOfSchedule(schedule)}`),
    );

    // Existing colours are looked up against the full original schedule list (not just the
    // affected rows) so an entity that already has a colour elsewhere in the timetable keeps it.
    const colouredBlocks = resolveScheduleColours(affectedBlocks, {
      defaultMenuId: defaultMenuFields.menuId,
      defaultMenuSetId: defaultMenuFields.menuSetId,
      defaultPriceSchemeId: defaultPriceSchemeId || null,
      existingSchedule: schedules,
    });

    const nameFor = (menuId?: string, menuSetId?: string, priceSchemeId?: string): string => {
      if (menuId !== undefined) return menus.find((menu) => menu.menuId === menuId)?.name ?? 'Unknown menu';
      if (menuSetId !== undefined) {
        return menuSets.find((menuSet) => menuSet.menu_set_id === menuSetId)?.name ?? 'Unknown menu set';
      }
      return priceSchemes.find((priceScheme) => priceScheme.id === priceSchemeId)?.name ?? 'Unknown price scheme';
    };

    const previewSchedules: ITimetableSchedule[] = colouredBlocks.map((block, index) => ({
      schedule_id: `preview-${block.day_of_week}-${trackOf(block)}-${index}`,
      day_of_week: block.day_of_week,
      start_time: block.start_time,
      end_time: block.end_time,
      name: nameFor(block.menu_id, block.menu_set_id, block.price_scheme_id),
      colour_order: block.colour_order,
      menu_id: block.menu_id ?? null,
      menu_set_id: block.menu_set_id ?? null,
      price_scheme_id: block.price_scheme_id ?? null,
    })) as ITimetableSchedule[];

    return [...untouchedSchedules, ...previewSchedules];
  })();

  const promotedSchedules = baseSchedules.map((schedule) =>
    promoteExplicitDefaultMatch(schedule, defaultMenuId, defaultPriceSchemeId),
  );

  return promotedSchedules.some((schedule, index) => schedule !== baseSchedules[index])
    ? promotedSchedules
    : baseSchedules;
};
